import {
  Mode,
  type Qualifier,
  type PlanQueryVariables,
} from '#/types/otp2types.generated';
import { tripModeConfig, otp2DefaultQuery } from '#/trip-planner/config';

/**
 * Common query parameters required for all modes
 */
const commonQueryParams: (keyof PlanQueryVariables)[] = [
  'date',
  'time',
  'fromPlace',
  'toPlace',
  'arriveBy',
  'modes',
  'searchWindow',
  'banned',
];

/**
 * Mode-specific query parameters for each mode.
 *
 * All Mode enum values are explicitly listed, even if their array is empty.
 * This ensures full type safety and prevents runtime errors if new modes are added.
 * It also makes the mapping exhaustive, so TypeScript can catch missing or misspelled modes at compile time.
 * This is considered best practice for Record types with enums as keys.
 */
type ModeSpecificParamsMap = Record<Mode, (keyof PlanQueryVariables)[]>;

const modeSpecificParams: ModeSpecificParamsMap = {
  [Mode.Transit]: ['walkReluctance', 'walkSpeed'],
  [Mode.Bicycle]: [
    'bikeReluctance',
    'bikeSpeed',
    'walkReluctance',
    'walkSpeed',
    'allowedVehicleRentalNetworks',
    'optimize',
  ],
  [Mode.Scooter]: [
    'bikeReluctance', // used for scooter reluctance
    'walkReluctance',
    'walkSpeed',
    'allowedVehicleRentalNetworks',
  ],
  [Mode.Car]: ['carReluctance', 'walkReluctance', 'walkSpeed'],
  [Mode.Airplane]: [],
  [Mode.Bus]: [],
  [Mode.CableCar]: [],
  [Mode.Carpool]: [],
  [Mode.Ferry]: [],
  [Mode.Funicular]: [],
  [Mode.Gondola]: [],
  [Mode.Rail]: [],
  [Mode.Subway]: [],
  [Mode.Tram]: [],
  [Mode.Trolleybus]: [],
  [Mode.Walk]: [],
  [Mode.Coach]: [],
  [Mode.Flex]: [],
  // Typescript compiler throws an error if all modes are not explicitly defined,
  // even if they are deprecated. Linter throws an error because these modes are deprecated.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  [Mode.Flexible]: [],
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  [Mode.LegSwitch]: [],
  [Mode.Monorail]: [],
  [Mode.Taxi]: [],
};

/**
 * Maps each mode to all the query parameters it requires (common + mode-specific)
 */
type ModeQueryParamsMap = Record<Mode, (keyof PlanQueryVariables)[]>;

/**
 * Combines common and mode-specific parameters for each mode
 */
const modeQueryParamsMap: ModeQueryParamsMap = Object.entries(
  modeSpecificParams
).reduce((map, [mode, specificParams]) => {
  return {
    ...map,
    [mode as Mode]: [...commonQueryParams, ...specificParams],
  };
}, {} as ModeQueryParamsMap);

/**
 * Gets only the necessary query parameters for the specified travel mode
 *
 * @param mode - The primary travel mode to get parameters for
 * @param queryParams - The full query parameters object (defaults to otp2DefaultQuery)
 * @returns A filtered query parameters object containing only the relevant properties
 */
export function getQueryParamsByMode(
  mode: Mode,
  queryParams: PlanQueryVariables = otp2DefaultQuery
): PlanQueryVariables {
  // Get the list of parameter keys needed for this mode
  const requiredParams = modeQueryParamsMap[mode];

  // Create a filtered object with only the required parameters
  return requiredParams.reduce((filtered, param) => {
    return {
      ...filtered,
      [param]: queryParams[param],
    };
  }, {} as PlanQueryVariables);
}

/**
 * Validates if the given mode is supported in the tripModeConfig
 *
 * @param mode - The mode to validate
 * @returns True if the mode is supported, false otherwise
 */
export function isValidTripMode(mode: string): mode is Mode {
  return tripModeConfig.some(config => config.value === mode);
}

/**
 * Converts a complex query object to URL search parameters
 * @param queryObj The query object to convert
 * @returns URLSearchParams object containing the query parameters
 */
export function queryObjectToParams(
  queryObj: PlanQueryVariables
): URLSearchParams {
  try {
    const params = new URLSearchParams();
    Object.entries(queryObj).forEach(([key, value]) => {
      // Skip empty values
      if (value === null || value === undefined || value === '') return;

      if (Array.isArray(value)) {
        // Handle modes array with TransportMode objects
        if (key === 'modes') {
          value.forEach((transportMode, index) => {
            // Add the mode
            params.append(`modes[${index}].mode`, transportMode.mode);
            // Add the qualifier if it exists
            if (transportMode.qualifier) {
              params.append(
                `modes[${index}].qualifier`,
                transportMode.qualifier
              );
            }
          });
        } else {
          // For simple arrays like allowedVehicleRentalNetworks
          value.forEach(item => {
            params.append(`${key}[]`, item.toString());
          });
        }
      } else if (typeof value === 'object') {
        // Handle nested objects (like banned)
        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
          params.append(`${key}.${nestedKey}`, String(nestedValue));
        });
      } else {
        // Handle primitive values
        params.append(key, value.toString());
      }
    });
    return params;
  } catch (error) {
    console.error(
      `Error converting query object to params: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    // Return empty params or partial results depending on requirements
    return new URLSearchParams();
  }
}

/**
 * Converts URL search parameters to a PlanQueryVariables object
 *
 * The function expects parameters in a specific format:
 * - Simple parameters: 'date=2025-05-05'
 * - Nested objects: 'banned.agencies=TriMet'
 * - Simple arrays: 'allowedVehicleRentalNetworks[]=BIKETOWN'
 * - Complex arrays with objects:
 *   - 'modes[0].mode=BICYCLE'
 *   - 'modes[0].qualifier=RENT'
 *   - 'modes[1].mode=BUS'
 *
 * @param urlParams - URL search parameters as a string or URLSearchParams object
 * @param defaultValues - Optional default values to use for missing parameters (defaults to otp2DefaultQuery)
 * @returns A PlanQueryVariables object populated from URL parameters
 */
export function paramsToQueryObject(
  urlParams: string | URLSearchParams,
  defaultValues: PlanQueryVariables = otp2DefaultQuery
): PlanQueryVariables {
  const params =
    typeof urlParams === 'string' ? new URLSearchParams(urlParams) : urlParams;

  // If there are URL params, start with an empty object instead of
  // default values since more nuanced coalescing happens later
  const result = {
    ...(params.size ? {} : defaultValues),
  } as PlanQueryVariables;

  // Helper function to get default value for a key if it exists
  const getDefaultValue = (key: keyof PlanQueryVariables) => {
    return defaultValues[key];
  };

  // First pass: process all parameters except modes qualifiers
  // This ensures we create the modes array entries first before adding qualifiers
  Array.from(params.entries()).forEach(([key, value]) => {
    const paramKey = key as keyof PlanQueryVariables;

    // Skip modes[].qualifier entries for now, will handle in second pass
    // Example format: 'modes[0].qualifier=RENT'
    if (key.startsWith('modes[') && key.includes('].qualifier')) {
      return;
    }

    // Handle array brackets notation like modes[0].mode or allowedVehicleRentalNetworks[]
    if (key.includes('[')) {
      // Handle TransportMode objects in modes array
      // Example format: 'modes[0].mode=BICYCLE'
      if (key.startsWith('modes[') && key.includes('].mode')) {
        const startIndex = key.indexOf('[') + 1;
        const endIndex = key.indexOf(']');
        const indexStr = key.substring(startIndex, endIndex);
        const index = parseInt(indexStr, 10);
        if (!Number.isNaN(index)) {
          if (!Array.isArray(result.modes)) {
            result.modes = [];
          }
          result.modes[index] = { mode: value as Mode };
        }
      } else if (key.endsWith('[]')) {
        // Handle simple arrays like allowedVehicleRentalNetworks[]
        // Example format: 'allowedVehicleRentalNetworks[]=BIKETOWN'
        const baseKey = key.slice(0, -2) as keyof PlanQueryVariables;
        if (!Array.isArray(result[baseKey])) {
          result[baseKey] = [];
        }
        (result[baseKey] as string[]).push(value);
      }
    } else if (key.includes('.')) {
      // Handle nested objects like banned.agencies
      // Example format: 'banned.agencies=TriMet'
      const [objectKey, nestedKey] = key.split('.') as [
        keyof PlanQueryVariables,
        string
      ];
      if (typeof result[objectKey] !== 'object' || result[objectKey] === null) {
        result[objectKey] = {};
      }
      (result[objectKey] as Record<string, string>)[nestedKey] = value;
    } else if (paramKey in defaultValues) {
      // Handle primitive values with type conversion
      const defaultValue = getDefaultValue(paramKey);

      if (typeof defaultValue === 'boolean') {
        // Convert string to boolean
        result[paramKey] = value === 'true';
      } else if (typeof defaultValue === 'number') {
        // Convert string to number
        result[paramKey] = parseFloat(value);
      } else {
        // String values
        result[paramKey] = value as never;
      }
    }
  });

  // Second pass: process modes qualifier entries
  // This ensures that the modes array is fully initialized before adding qualifiers
  Array.from(params.entries()).forEach(([key, value]) => {
    // Handle modes[].qualifier entries
    // Example format: 'modes[0].qualifier=RENT'
    if (key.startsWith('modes[') && key.includes('].qualifier')) {
      const startIndex = key.indexOf('[') + 1;
      const endIndex = key.indexOf(']');
      const indexStr = key.substring(startIndex, endIndex);
      const index = parseInt(indexStr, 10);
      if (
        !Number.isNaN(index) &&
        Array.isArray(result.modes) &&
        result.modes[index]
      ) {
        result.modes[index].qualifier = value as Qualifier;
      }
    }
  });

  return result;
}

/**
 * Converts a query string to a PlanQueryVariables object
 *
 * @param queryString - Query string without the leading '?'
 * @returns PlanQueryVariables object populated from the query parameters
 */
export function parseQueryString(queryString: string): PlanQueryVariables {
  try {
    // Convert to URLSearchParams and then to PlanQueryVariables
    return paramsToQueryObject(new URLSearchParams(queryString));
  } catch (error) {
    console.error(
      `Error parsing query string: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    // Return default query in case of error
    return { ...otp2DefaultQuery };
  }
}
